import React, { useEffect, useRef, useState } from "react";
import "./Contact.css";

const CONTACT_EMAIL = "academicgroupofschools@gmail.com";
const CONTACT_PHONE = "+923047630462";
const CONTACT_PHONE_DISPLAY = "+92 304 7630462";
const CONTACT_ADDRESS = "Chowk Shah Abbas, Suraj Kund Road, Multan";
const MAP_EMBED_URL =
  "https://www.google.com/maps?q=" +
  encodeURIComponent(CONTACT_ADDRESS + ", Pakistan") +
  "&output=embed";
const MAP_LINK_URL =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent(CONTACT_ADDRESS + ", Pakistan");

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Inline fallback: kisi bhi global CSS rule se zyada strong
const SUBMIT_BTN_STYLE = {
  background: "linear-gradient(135deg, #0f172a 0%, #3b82f6 100%)",
  backgroundColor: "#2563eb",
  color: "#ffffff",
  width: "100%",
  minHeight: "52px",
};

const PinIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" />
  </svg>
);

const PhoneIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21 12.27 12.27 0 003.88 1.17 1 1 0 01.89 1V20a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.5a1 1 0 011 .89 12.27 12.27 0 001.17 3.88 1 1 0 01-.21 1.11l-2.84 2.91z" />
  </svg>
);

const MailIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 2v.01L12 13 4 6.01V6h16zM4 18v-8l8 5 8-5v8H4z" />
  </svg>
);

const CheckIcon = () => (
  <svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState({});
  const timers = useRef([]);

  // Unmount hone par saare pending timers clear karo
  useEffect(() => {
    const pendingTimers = timers.current;
    return () => pendingTimers.forEach(clearTimeout);
  }, []);

  // Success message 5 second baad hide ho
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(""), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!EMAIL_REGEX.test(formData.email.trim())) newErrors.email = "Please enter a valid email";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSending) return;

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSending(true);
    const t = setTimeout(() => {
      setSuccessMessage("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
      setErrors({});
      setIsSending(false);
    }, 800);
    timers.current.push(t);
  };

  return (
    <section className="contact-section">
      <div className="contact-wrapper">
        {/* Header */}
        <header className="contact-header">
          <div className="header-accent" aria-hidden="true"></div>
          <h1 className="header-title">Get In Touch</h1>
          <p className="header-subtitle">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </header>

        {/* Main Container */}
        <div className="contact-container">
          {/* Form Section */}
          <div className="form-section">
            <form className="contact-form" onSubmit={handleSubmit} noValidate aria-label="Contact form">
              <h2 className="form-title">Send us a Message</h2>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="contact-name">
                    Full Name <span className="required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="John Doe"
                    name="name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? "input-error" : ""}
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "contact-name-error" : undefined}
                  />
                  {errors.name && (
                    <span id="contact-name-error" className="error-text" role="alert">
                      {errors.name}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="contact-email">
                    Email Address <span className="required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="you@example.com"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "input-error" : ""}
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "contact-email-error" : undefined}
                  />
                  {errors.email && (
                    <span id="contact-email-error" className="error-text" role="alert">
                      {errors.email}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contact-message">
                  Message <span className="required" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="contact-message"
                  placeholder="Tell us more about your inquiry..."
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? "input-error" : ""}
                  aria-required="true"
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? "contact-message-error" : undefined}
                />
                {errors.message && (
                  <span id="contact-message-error" className="error-text" role="alert">
                    {errors.message}
                  </span>
                )}
              </div>

              <button
                type="submit"
                disabled={isSending}
                className="submit-btn contact-submit-btn"
                style={SUBMIT_BTN_STYLE}
              >
                <span style={{ color: "#ffffff" }}>{isSending ? "Sending..." : "Send Message"}</span>
                {!isSending && (
                  <span className="btn-arrow" aria-hidden="true" style={{ color: "#ffffff" }}>
                    →
                  </span>
                )}
              </button>

              {successMessage && (
                <div className="success-alert" role="status">
                  <CheckIcon />
                  <span>{successMessage}</span>
                </div>
              )}
            </form>
          </div>

          {/* Info Section */}
          <div className="info-section">
            <div className="info-card">
              <h2 className="info-title">Contact Information</h2>

              <div className="info-items">
                <a href={`tel:${CONTACT_PHONE}`} className="info-item">
                  <div className="info-icon phone">
                    <PhoneIcon />
                  </div>
                  <div className="info-content">
                    <h3>Phone</h3>
                    <p className="info-value">{CONTACT_PHONE_DISPLAY}</p>
                  </div>
                </a>

                <a href={`mailto:${CONTACT_EMAIL}`} className="info-item">
                  <div className="info-icon mail">
                    <MailIcon />
                  </div>
                  <div className="info-content">
                    <h3>Email</h3>
                    <p className="info-value email-value">{CONTACT_EMAIL}</p>
                  </div>
                </a>

                <a
                  href={MAP_LINK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="info-item"
                >
                  <div className="info-icon location">
                    <PinIcon />
                  </div>
                  <div className="info-content">
                    <h3>Location</h3>
                    <p className="info-value">{CONTACT_ADDRESS}</p>
                  </div>
                </a>
              </div>

              {/* Map Section */}
              <div className="map-wrapper">
                <h2 className="map-title">Find Us On Map</h2>
                <div className="map-container">
                  <iframe
                    src={MAP_EMBED_URL}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="School location map"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;